import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { SearchableSelect } from "~/components/ui/searchable-select";
import { ALIACE_SPA_ID } from "~/constants/companies";
import { useAccounts } from "~/hooks/contabilidad/useAccounts";
import { usePaySupplierInvoices } from "~/hooks/purchasing";
import { useProfileStore } from "~/store/profileStore";
import { formatCurrency } from "~/utils/helpers";

import type { PayableDocument, PayItem } from "~/types/purchasing";

const DEFAULT_BANK_CODE = "1101010"; // Banco de Chile

interface PagarDocumentosDialogProps {
  docs: PayableDocument[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired after a successful payment (before the dialog closes). */
  onPaid?: () => void;
}

const clp = (n: number) => `$${formatCurrency(n)}`;
const digitsOnly = (s: string | null | undefined) =>
  (s ?? "").toUpperCase().replace(/[^0-9K]/g, "");
const rowKey = (d: PayableDocument) => `${d.source}-${d.doc_id}`;

// Monto a aplicar por documento: texto con separadores de miles, se parsea a entero (CLP sin
// decimales). Permite editar el abono parcial de una factura o la aplicación parcial de una NC.
function MontoInput({
  value,
  invalid,
  onChange,
}: {
  value: number;
  invalid: boolean;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <span className="text-xs text-muted-foreground">$</span>
      <input
        type="text"
        inputMode="numeric"
        value={value ? formatCurrency(value) : ""}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^\d]/g, "");
          onChange(digits ? parseInt(digits, 10) : 0);
        }}
        className={`h-8 w-28 rounded-md border bg-white px-2 text-right font-mono text-sm outline-none transition focus:ring-2 focus:ring-primary/20 ${
          invalid
            ? "border-red-400 text-red-700"
            : "border-gray-300 text-gray-800"
        }`}
      />
    </div>
  );
}

export function PagarDocumentosDialog({
  docs,
  open,
  onOpenChange,
  onPaid,
}: PagarDocumentosDialogProps) {
  // A payment batch must be a single empresa — the chart comes from the documents' company.
  const multiCompany = useMemo(
    () => new Set(docs.map((d) => d.company_id)).size > 1,
    [docs]
  );
  const companyId = docs[0]?.company_id ?? ALIACE_SPA_ID;
  const { data: accounts = [] } = useAccounts({
    company_id: companyId,
    is_active: true,
  });
  const role = useProfileStore((s) => s.role);
  const pay = usePaySupplierInvoices();
  const [bankId, setBankId] = useState("");
  const [payDate, setPayDate] = useState(() => dayjs().format("YYYY-MM-DD"));

  // Monto a aplicar editable por documento (clave = rowKey). Las facturas parten en su saldo
  // completo; las NC se topan al total por pagar del mismo proveedor en la selección, para que el
  // asiento abra cuadrado aun cuando la NC supera a la(s) factura(s). Todo queda editable: el
  // usuario puede bajar el abono de una factura (pago parcial) o el monto aplicado de una NC
  // (aplicación parcial de la nota de crédito).
  const [amounts, setAmounts] = useState<Record<string, number>>({});
  const docsKey = useMemo(() => docs.map(rowKey).join("|"), [docs]);
  useEffect(() => {
    const budget = new Map<string, number>(); // supplier_id -> por pagar restante por cruzar
    for (const d of docs) {
      if (Number(d.payable_clp) > 0) {
        budget.set(
          d.supplier_id,
          (budget.get(d.supplier_id) ?? 0) + Math.abs(Number(d.payable_clp))
        );
      }
    }
    const next: Record<string, number> = {};
    for (const d of docs) {
      const saldo = Math.abs(Number(d.payable_clp));
      if (Number(d.payable_clp) < 0) {
        const remaining = Math.max(budget.get(d.supplier_id) ?? 0, 0);
        // Con deuda del mismo proveedor que cruzar, la NC se topa a esa deuda (cruce sin banco). Sin
        // deuda que cruzar, la NC se cobra completa como devolución que entra al banco.
        const applied = remaining > 0 ? Math.min(saldo, remaining) : saldo;
        budget.set(d.supplier_id, Math.max(remaining - applied, 0));
        next[rowKey(d)] = applied;
      } else {
        next[rowKey(d)] = saldo;
      }
    }
    setAmounts(next);
    // docsKey captures the identity of the selected docs; re-seed defaults on a new selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docsKey]);

  const setAmount = (key: string, n: number) =>
    setAmounts((prev) => ({ ...prev, [key]: n }));

  // Cuenta de pago = la contrapartida (haber) del asiento. Mostramos las cuentas de activo
  // (bancos/caja 1101·, cheques en cartera/protestados 1104·, anticipos 1105·, etc.) y las cuentas
  // financieras 2101· (tarjetas de crédito, líneas de crédito, leasing) para poder pagar desde
  // cualquier medio, no solo bancos. El orden por código deja bancos/caja arriba y las tarjetas abajo.
  const bankOptions = useMemo(
    () =>
      accounts
        .filter(
          (a) =>
            a.account_type === "activo" || digitsOnly(a.code).startsWith("2101")
        )
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((a) => ({
          value: a.id,
          label: `${a.code} · ${a.name}`,
          searchTerms: `${a.code} ${a.name}`,
        })),
    [accounts]
  );

  useEffect(() => {
    if (bankId) return;
    const def = accounts.find((a) => a.code === DEFAULT_BANK_CODE);
    if (def) setBankId(def.id);
  }, [accounts, bankId]);

  const bankAccount = accounts.find((a) => a.id === bankId);

  // Preview: one line per document on Proveedores — payables as Debe, NC (crédito) as Haber — plus a
  // single aggregated Cr Banco line for the net cash. A full cross (net 0) posts no bank line.
  const rows = useMemo(
    () =>
      docs.map((d) => {
        const code = digitsOnly(d.ap_account_code) || "2103001";
        const acct = accounts.find((a) => digitsOnly(a.code) === code);
        const key = rowKey(d);
        const saldo = Math.abs(Number(d.payable_clp));
        const amount = amounts[key] ?? saldo;
        return {
          key,
          doc: d,
          credit: Number(d.payable_clp) < 0, // NC: payable_clp negativo
          saldo,
          amount, // monto a aplicar (editable); nunca puede superar el saldo
          invalid: amount <= 0 || amount > saldo + 0.5,
          label: acct ? `${acct.code} · ${acct.name}` : "Proveedores",
        };
      }),
    [docs, accounts, amounts]
  );
  const anyInvalid = useMemo(() => rows.some((r) => r.invalid), [rows]);
  const totalDebe = useMemo(
    () => rows.filter((r) => !r.credit).reduce((s, r) => s + r.amount, 0),
    [rows]
  );
  const totalCredit = useMemo(
    () => rows.filter((r) => r.credit).reduce((s, r) => s + r.amount, 0),
    [rows]
  );
  // Efectivo neto al banco = deuda − NC. neto>0 → Cr Banco (pago, sale plata). neto<0 → Dr Banco
  // (devolución del proveedor, entra plata). neto 0 → cruce sin movimiento de banco.
  const netCash = totalDebe - totalCredit;
  const isRefund = netCash < 0;
  const bankCredit = netCash > 0 ? netCash : 0; // Cr Banco (pago)
  const bankDebit = netCash < 0 ? -netCash : 0; // Dr Banco (devolución)

  // No mezclar pagos (neto>0) y devoluciones (neto<0) de distintos proveedores en un mismo
  // comprobante: la devolución de un proveedor no puede netear la deuda de otro sin pasar por el
  // banco (espeja el guard del RPC). Un comprobante de un solo proveedor nunca se bloquea.
  const mixedDirection = useMemo(() => {
    const netBySupplier = new Map<string, number>(); // pay − cred por proveedor
    for (const r of rows) {
      const cur = netBySupplier.get(r.doc.supplier_id) ?? 0;
      netBySupplier.set(r.doc.supplier_id, cur + (r.credit ? -r.amount : r.amount));
    }
    const nets = Array.from(netBySupplier.values());
    return nets.some((n) => n > 0.01) && nets.some((n) => n < -0.01);
  }, [rows]);

  // Defensa: una factura sin contabilizar (asiento publicado) no se puede pagar. El RPC también la
  // rechaza; esto evita siquiera intentarlo desde el diálogo.
  const hasUnposted = useMemo(() => docs.some((d) => !d.is_posted), [docs]);

  const canPay =
    !!bankId &&
    rows.length > 0 &&
    !anyInvalid &&
    !mixedDirection &&
    !multiCompany &&
    !hasUnposted &&
    !pay.isPending;

  const handleConfirm = () => {
    if (!canPay) return;
    const items: PayItem[] = rows.map((r) => ({
      source: r.doc.source,
      doc_id: r.doc.doc_id,
      amount: Math.round(r.amount),
    }));
    const ref =
      rows.length === 1
        ? `pago ${rows[0].doc.supplier_name} Fac ${rows[0].doc.folio ?? ""}`.trim()
        : `pago nómina proveedores ${dayjs(payDate).format("DD/MM/YYYY")}`;
    pay.mutate(
      {
        paymentDate: payDate,
        bankAccountId: bankId,
        items,
        ref,
        isAdmin: role === "superadmin" || role === "admin",
      },
      {
        onSuccess: () => {
          onPaid?.();
          onOpenChange(false);
        },
      }
    );
  };

  const isNomina = docs.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isNomina ? "Pagar documentos (nómina)" : "Pagar documento"}
          </DialogTitle>
          <DialogDescription>
            {(() => {
              const amt = isRefund ? `devolución ${clp(-netCash)}` : clp(netCash);
              return isNomina
                ? `${docs.length} documentos · ${amt}`
                : docs[0]
                  ? `${docs[0].supplier_name} · ${docs[0].folio ?? ""} · ${amt}`
                  : "";
            })()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                Cuenta / medio de pago
              </label>
              <SearchableSelect
                value={bankId}
                onValueChange={setBankId}
                options={bankOptions}
                placeholder="Selecciona la cuenta…"
                emptyMessage="Sin cuentas"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                Fecha de pago
              </label>
              <Input
                type="date"
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
                className="h-9"
              />
            </div>
          </div>

          <div className="max-h-72 overflow-auto rounded-lg border border-gray-300">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-100 text-gray-600">
                  <th className="px-3 py-2 text-left font-medium">Cuenta</th>
                  <th className="px-3 py-2 text-right font-medium">Debe</th>
                  <th className="px-3 py-2 text-right font-medium">Haber</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.key}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="px-3 py-2 text-gray-800">
                      {r.label}
                      <div className="text-xs text-muted-foreground">
                        {r.credit
                          ? `aplicación NC ${r.doc.folio ?? ""} · ${r.doc.supplier_name}`
                          : `pago ${r.doc.supplier_name}${r.doc.folio ? ` Fac ${r.doc.folio}` : ""}`}
                        {" · saldo "}
                        {clp(r.saldo)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {r.credit ? (
                        "-"
                      ) : (
                        <MontoInput
                          value={r.amount}
                          invalid={r.invalid}
                          onChange={(n) => setAmount(r.key, n)}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {r.credit ? (
                        <MontoInput
                          value={r.amount}
                          invalid={r.invalid}
                          onChange={(n) => setAmount(r.key, n)}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
                {(bankCredit > 0 || bankDebit > 0) && (
                  <tr className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-2 text-gray-800">
                      {bankAccount
                        ? `${bankAccount.code} · ${bankAccount.name}`
                        : "Banco"}
                      {isRefund && (
                        <div className="text-xs text-muted-foreground">
                          devolución del proveedor
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {bankDebit > 0 ? clp(bankDebit) : "-"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {bankCredit > 0 ? clp(bankCredit) : "-"}
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 bg-gray-50 font-medium">
                  <td className="px-3 py-2 text-right text-gray-600">Totales</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {clp(totalDebe + bankDebit)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {clp(totalCredit + bankCredit)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="text-xs text-muted-foreground">
            Edita el monto a aplicar de cada documento para registrar pagos o
            cruces parciales (por ejemplo, aplicar solo parte de una nota de
            crédito). El monto no puede superar el saldo del documento.
          </p>

          {multiCompany ? (
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4" /> Los documentos son de distintas
              empresas — un pago debe ser de una sola empresa.
            </div>
          ) : hasUnposted ? (
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4" /> Hay facturas sin contabilizar en
              la selección. Contabilízalas antes de pagarlas.
            </div>
          ) : anyInvalid ? (
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4" /> Hay montos a aplicar inválidos:
              deben ser mayores a 0 y no superar el saldo del documento.
            </div>
          ) : mixedDirection ? (
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4" /> No mezcles pagos y devoluciones de
              distintos proveedores en un mismo comprobante.
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4" /> El asiento cuadra
              {netCash === 0
                ? " · cruce sin movimiento de banco"
                : isRefund
                  ? " · devolución al banco"
                  : ""}
            </div>
          )}

          <Badge variant="secondary" className="bg-gray-100 text-gray-600">
            Diario PAGO_PROVEEDORES NACIONALES · Borrador (lo publica Contabilidad
            en Movimientos)
          </Badge>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!canPay}>
            {pay.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar borrador
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
